/**
 * Глобальный поиск owner: клиенты / боты / партнёры по одному запросу.
 * Клиенты и партнёры кликабельны → карточка. Главный сценарий — найти клиента
 * и поменять ему баланс / заморозить подписку.
 */

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import {
  Search as SearchIcon,
  Loader2,
  User as UserIcon,
  Bot as BotIcon,
  Users,
  ChevronRight,
} from 'lucide-react';
import { getWebApp } from '@/lib/telegram';
import { useDebounce } from '@/lib/use-debounce';

export const Route = createFileRoute('/search')({ component: GlobalSearch });

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const debounced = useDebounce(q.trim(), 300);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.show();
    const onBack = () => navigate({ to: '/' });
    tg?.BackButton.onClick(onBack);
    return () => {
      tg?.BackButton.offClick(onBack);
      tg?.BackButton.hide();
    };
  }, [navigate]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.search(debounced),
    enabled: debounced.length >= 1,
    placeholderData: keepPreviousData,
  });

  const empty =
    debounced.length >= 1 &&
    !isFetching &&
    data &&
    data.clients.length === 0 &&
    data.bots.length === 0 &&
    data.partners.length === 0;

  return (
    <div className="p-4 space-y-4 pb-32">
      <h2 className="text-2xl font-bold tracking-tight px-2 pt-2">Поиск</h2>

      <div className="relative">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        {isFetching && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
        )}
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="@username, tg_id, имя бота, партнёр…"
          className="w-full bg-white/5 border border-white/10 rounded-2xl pl-11 pr-11 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {debounced.length < 1 && (
        <p className="text-center text-sm text-muted-foreground py-10">
          Введи @username, Telegram ID, название бота или имя партнёра
        </p>
      )}

      {empty && (
        <p className="text-center text-sm text-muted-foreground py-10">Ничего не найдено</p>
      )}

      {data && data.clients.length > 0 && (
        <Section title="Клиенты" icon={<UserIcon className="w-3.5 h-3.5" />}>
          {data.clients.map((c) => (
            <Link key={c.id} to="/clients/$id" params={{ id: c.id }} preload="intent">
              <HitRow
                title={
                  c.tg_username
                    ? `@${c.tg_username}`
                    : c.tg_user_id != null
                      ? `tg_id ${c.tg_user_id}`
                      : (c.email ?? 'без Telegram')
                }
                sub={c.partner_name || '—'}
                badge={
                  c.is_blocked ? (
                    <Badge cls="bg-red-500/15 text-red-500">заблокирован</Badge>
                  ) : !c.is_active ? (
                    <Badge cls="bg-yellow-500/15 text-yellow-500">бан</Badge>
                  ) : null
                }
                clickable
              />
            </Link>
          ))}
        </Section>
      )}

      {data && data.partners.length > 0 && (
        <Section title="Партнёры" icon={<Users className="w-3.5 h-3.5" />}>
          {data.partners.map((p) => (
            <Link key={p.id} to="/partners/$id" params={{ id: p.id }} preload="intent">
              <HitRow
                title={p.display_name}
                sub={p.tg_user_id ? `tg_id ${p.tg_user_id}` : '—'}
                badge={
                  !p.is_active ? <Badge cls="bg-red-500/15 text-red-500">бан</Badge> : null
                }
                clickable
              />
            </Link>
          ))}
        </Section>
      )}

      {data && data.bots.length > 0 && (
        <Section title="Боты" icon={<BotIcon className="w-3.5 h-3.5" />}>
          {data.bots.map((b) => (
            <Link key={b.id} to="/bots/$id" params={{ id: b.id }} preload="intent">
              <HitRow
                title={`@${b.username}`}
                sub={`${b.title} · ${b.partner_name || '—'}`}
                badge={
                  !b.is_active ? (
                    <Badge cls="bg-white/10 text-muted-foreground">{b.status}</Badge>
                  ) : (
                    <Badge cls="bg-green-500/15 text-green-500">{b.status}</Badge>
                  )
                }
                clickable
              />
            </Link>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 flex items-center gap-1.5">
        {icon}
        {title}
      </h3>
      <Card>
        <CardContent className="p-2 divide-y divide-white/5">{children}</CardContent>
      </Card>
    </div>
  );
}

function HitRow({
  title,
  sub,
  badge,
  clickable,
}: {
  title: string;
  sub: string;
  badge?: React.ReactNode;
  clickable?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3 active:bg-white/5 transition-colors rounded-xl">
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm truncate">{title}</div>
        <div className="text-[11px] text-muted-foreground truncate">{sub}</div>
      </div>
      {badge}
      {clickable && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
    </div>
  );
}

function Badge({ children, cls }: { children: React.ReactNode; cls: string }) {
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cls}`}>
      {children}
    </span>
  );
}
